/**
 * Die Routen und die Verdrahtung der Hülle.
 *
 * Bewusst von Hand statt dateibasiert: Die Zahl der Bereiche ist eine
 * Architekturentscheidung (ADR 0011). Wer einen siebten anlegen will, muss ihn
 * hier hinschreiben — und merkt dabei, dass er es tut. Ein neues Feature
 * bekommt einen **Reiter** in einer bestehenden Arbeitsfläche; nur ein
 * wirklich neuer Bereich braucht hier eine Zeile, und dafür ein ADR.
 *
 * Zustand, der geteilt werden können muss, steht in der URL: Bereich, aktiver
 * Reiter eines Objekts, Filter und Suchanfrage. Beides gehört nicht in einen
 * Komponentenzustand, weil ein Link darauf sonst beim Empfänger etwas anderes
 * zeigt.
 *
 * Hier werden auch die beiden Slots der `AppShell` gefüllt — Symbolleiste und
 * Kontext-Sidebar. Sie liegen in `components/` und dürfen `features/` nicht
 * importieren; diese Datei (`app`-Ebene) darf beides.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  Link,
  Navigate,
  Outlet,
  redirect,
  useRouterState,
} from '@tanstack/react-router'
import { Building2, CalendarDays, Euro, FileText, Search as SearchIcon, Zap } from 'lucide-react'
import { useState } from 'react'

import { contractFacetSearch, contractSearch, energieSearch, parseAppSearch } from '@/lib/search-params'

import { AppShell } from '@/components/AppShell'
import { ConfirmationStrip } from '@/components/ConfirmationStrip'
import { ContextSidebar } from '@/components/ContextSidebar'
import { SidebarItem } from '@/components/SidebarItem'
import { SidebarSection } from '@/components/SidebarSection'
import { PopoverMenu } from '@/components/PopoverMenu'
import { Rail, type RailItem } from '@/components/Rail'
import { useMe } from '@/features/access/api'
import { AccountPasswordSheet } from '@/features/access/AccountPasswordSheet'
import { useAuth } from '@/features/auth/auth-context'
import { useBillingSummary } from '@/features/billing/api'
import { useDocumentCategories, useDocumentSummary } from '@/features/documents/api'
import { useMeters } from '@/features/energy/api'
import { HouseFilter } from '@/features/energy/HouseFilter'
import { ObjectTree } from '@/features/objects/ObjectTree'
import { PrivateAreaSheet } from '@/features/objects/PrivateAreaSheet'
import { PropertySheet } from '@/features/properties/PropertySheet'
import { useTicketSummary } from '@/features/tasks/api'
import { parseKalenderSearch, toKalenderView } from '@/lib/kalender-view'
import type { KalenderSearch } from '@/lib/kalender-view'
import { useIsDesktopViewport } from '@/lib/viewport'
import { Access } from '@/routes/Access'
import { AccessUser } from '@/routes/AccessUser'
import { Dokumente, isCategoryView, toDokumenteView } from '@/routes/Dokumente'
import { Energie } from '@/routes/Energie'
import { Geld } from '@/routes/Geld'
import { Kalender } from '@/routes/Kalender'
import { ContractModal } from '@/routes/ContractModal'
import { useEntityProjectionRefresh } from '@/features/objects/api'
import { ObjectDetail } from '@/routes/ObjectDetail'
import { ObjectList } from '@/routes/ObjectList'
import { Search } from '@/routes/Search'

/**
 * Die Bereiche, die es **gibt**. ADR 0033 (Spec 45) hat *Aufgaben* durch
 * *Energie* ersetzt: Kalender · Geld · Energie · Dokumente · Objekte. Die
 * Aufgaben stehen im *Kalender* (Ansichten „Überfällig" und „Meine
 * Aufgaben" der Kontext-Sidebar). **Kalender** (Spec 07) ist der **erste**
 * Eintrag, Route `/` — dieselbe Stelle, die zuvor „Heute" trug. **Energie**
 * ist in diesem Schnitt ein leerer Bereich (Spec 45, R1/R13: Kopf und
 * Leerzustand, keine Preiskapsel im Web).
 *
 * `attention` steht hier nicht fest: Es kommt aus `GET /billing/summary`
 * (Spec 04a), `GET /tickets/summary` (Spec 06a, jetzt am *Kalender*, R4) bzw.
 * `GET /documents/summary` (Spec 09) und wird unten in `AppRail` eingesetzt —
 * `RAIL_ITEMS` bleibt der unveränderliche Teil (Reihenfolge, Ziel, Symbol),
 * die Zahl ist der einzige Teil, der pro Rendern neu kommt. *Energie* trägt nie
 * ein `attention`.
 *
 * *Suchen* ist Übergangszustand. Der Skill sieht dafür `Cmd+K` vor; solange die
 * Befehlspalette nicht existiert, bleibt der Bildschirm aus M0b erreichbar —
 * und zwar mit denselben drei Zielen wie auf iOS, damit beide Clients gleich
 * bedient werden.
 */
/** Exportiert für `router.energie.test.ts` (AK-1283: Reihenfolge der Bereiche). */
export const RAIL_ITEMS_BASE: readonly Omit<RailItem, 'attention'>[] = [
  { to: '/', label: 'Kalender', testId: 'rail-kalender', Icon: CalendarDays },
  { to: '/geld', label: 'Geld', testId: 'rail-geld', Icon: Euro },
  { to: '/energie', label: 'Energie', testId: 'rail-energie', Icon: Zap },
  { to: '/dokumente', label: 'Dokumente', testId: 'rail-dokumente', Icon: FileText },
  { to: '/objekt', label: 'Objekte', testId: 'rail-objekte', Icon: Building2 },
  { to: '/suchen', label: 'Suchen', testId: 'rail-suchen', Icon: SearchIcon },
]

/**
 * Füllt `attention` an `rail-geld` aus `GET /billing/summary`, an
 * `rail-kalender` aus `GET /tickets/summary` und an `rail-dokumente` aus
 * `GET /documents/summary` (ADR 0011: „Kein Bereich ohne eigenen
 * Zähl-Endpunkt") — nie aus einem `.filter().length` im Client. Lädt eine
 * Kennzahl noch oder schlägt sie fehl, bleibt ihr `attention` `undefined`;
 * der Punkt an der Symbolleiste erscheint erst ab einem tatsächlichen Wert
 * über 0 (`Rail.tsx`).
 */
function AppRail() {
  const isDesktop = useIsDesktopViewport()
  const billingSummary = useBillingSummary()
  const billingAttention = billingSummary.data?.attention
  const ticketSummary = useTicketSummary()
  const ticketAttention = ticketSummary.data?.attention
  const documentSummary = useDocumentSummary()
  const documentAttention = documentSummary.data?.attention

  // `attention` bekommt nur dann einen Schlüssel im Objekt, wenn ein echter
  // Wert da ist — mit `exactOptionalPropertyTypes` ist `{ attention:
  // undefined }` etwas anderes als „der Schlüssel fehlt" und keins von
  // beidem darf für ein optionales Feld ausdrücklich `undefined` tragen.
  const items: readonly RailItem[] = RAIL_ITEMS_BASE.map((item) => {
    if (item.testId === 'rail-geld' && billingAttention !== undefined) return { ...item, attention: billingAttention }
    if (item.testId === 'rail-kalender' && ticketAttention !== undefined) {
      return { ...item, attention: ticketAttention }
    }
    if (item.testId === 'rail-dokumente' && documentAttention !== undefined) {
      return { ...item, attention: documentAttention }
    }
    return item
  })

  // Unter 768px war die Symbolleiste immer schon nur per CSS versteckt
  // (`Rail.tsx: hidden md:flex`), mit `AccountMenu` weiter im Baum — seit
  // Spec 25 (B1) braucht `rail-konto` dort einen zweiten, mobilen Platz
  // (`RootLayout`), und zwei Kopien mit demselben `data-testid` stünden
  // beide im Dokument. Deshalb mountet die Symbolleiste am Telefon gar
  // nicht mehr, statt nur unsichtbar zu bleiben.
  if (!isDesktop) return null

  return <Rail items={items} account={<AccountMenu variant="rail" />} />
}

/**
 * Die Kontext-Sidebar — nur ab 768px gemountet. `AppShell` liegt in
 * `components/` und darf `features/` nicht importieren; diese Datei (`app`-
 * Ebene) darf beides, also verdrahtet sie hier statt in `AppShell` selbst.
 *
 * **Sie wechselt mit dem Bereich** (ADR 0011). Bis hierher stand an dieser
 * Stelle immer der Objektbaum — auch auf *Geld*, wo er keine Frage der
 * Ansicht beantwortet: Er sagt, was in einem Gebäude steckt, nicht, welche
 * Forderungen über alle Objekte hinweg überfällig sind.
 *
 * Ein Bereich ohne eigenen Sidebar-Inhalt bekommt **keine leere Spalte,
 * sondern gar keine** — der Inhalt nimmt die Breite. Dieselbe Regel wie in
 * der Symbolleiste: Eine Fläche, die nichts trägt, ist ein Versprechen, das
 * die Oberfläche nicht hält. *Kalender* bekommt bewusst **keine** — vier
 * Wochen sind vier Wochen, keine gespeicherten Ansichten (Spec 07,
 * „Kontext-Sidebar: keine").
 *
 * `PropertySheet` (`features/properties`) mountet hier statt in `ObjectTree`
 * (`features/objects`) selbst — Features kennen einander nicht
 * (`apps/web/CLAUDE.md`), komponiert wird an dieser Stelle.
 */
function Sidebar() {
  const isDesktop = useIsDesktopViewport()
  const path = useRouterState({ select: (state) => state.location.pathname })
  const [isPropertySheetOpen, setIsPropertySheetOpen] = useState(false)
  const [isPrivateAreaSheetOpen, setIsPrivateAreaSheetOpen] = useState(false)

  if (!isDesktop) return null

  // *Geld* und *Dokumente* tragen ihre gespeicherten Ansichten, *Objekte*
  // den Baum.
  //
  // *Kalender* trägt seit Spec 45 drei Ansichten (vier Wochen, Überfällig,
  // Meine Aufgaben) — die zwei Aufgabenlisten, die *Aufgaben* verlor. *Energie*
  // trägt seit Spec 46a den Haus-Filter der Zählerliste. Ohne Sidebar bleiben:
  // *Suchen* und `/zugriff`; `/zugriff` hängt am Konto, nicht an einem Bereich
  // (ADR 0011).
  if (path === '/') return <KalenderSidebar />
  if (path.startsWith('/geld')) return <GeldSidebar />
  if (path.startsWith('/energie')) return <EnergieSidebar />
  if (path.startsWith('/dokumente')) return <DokumenteSidebar />
  if (!path.startsWith('/objekt')) return null

  return (
    <>
      <ContextSidebar title="Objekte">
        <ObjectTree
          onAddProperty={() => {
            setIsPropertySheetOpen(true)
          }}
          onAddPrivateArea={() => {
            setIsPrivateAreaSheetOpen(true)
          }}
        />
      </ContextSidebar>
      <PropertySheet open={isPropertySheetOpen} onOpenChange={setIsPropertySheetOpen} />
      <PrivateAreaSheet open={isPrivateAreaSheetOpen} onOpenChange={setIsPrivateAreaSheetOpen} />
    </>
  )
}

/**
 * Die Ansichten des Bereichs *Kalender* (Spec 45, Entscheidung 5): „Vier
 * Wochen" (Vorgabe) · „Überfällig" · „Meine Aufgaben" — dritte Instanz der
 * Sidebar-Bauform. Die Zahlen kommen aus `GET /tickets/summary`, demselben
 * Aufruf wie `rail-kalender-attention` in `AppRail` (gleicher Query-Key, kein
 * zusätzlicher Netzwerkaufruf). `isActive` folgt `view` aus der URL.
 */
function KalenderSidebar() {
  const summary = useTicketSummary()
  const view = useRouterState({ select: (state) => toKalenderView((state.location.search as { view?: unknown }).view) })

  return (
    <ContextSidebar title="Kalender">
      <SidebarSection title="Ansichten">
        <SidebarItem to="/" search={{}} label="Vier Wochen" testId="kalender-view-wochen" isActive={view === undefined} />
        <SidebarItem
          to="/"
          search={{ view: 'ueberfaellig' }}
          label="Überfällig"
          testId="kalender-view-ueberfaellig"
          {...(summary.data === undefined ? {} : { count: summary.data.overdue })}
          isAlert
          isActive={view === 'ueberfaellig'}
        />
        <SidebarItem
          to="/"
          search={{ view: 'meine' }}
          label="Meine Aufgaben"
          testId="kalender-view-meine"
          {...(summary.data === undefined ? {} : { count: summary.data.assigned_to_me })}
          isActive={view === 'meine'}
        />
      </SidebarSection>
    </ContextSidebar>
  )
}

/**
 * Die Ansichten des Bereichs *Geld*.
 *
 * **Reiter und Ansicht sind zwei verschiedene Dinge**, und die Trennung ist
 * die Begründung für den Zuschnitt hier: Der *Reiter* wählt die Liste (offene
 * Posten oder Klärfälle, Spec 04c) — die Sidebar wählt den **Ausschnitt** einer
 * Liste. *Klärfälle* steht deshalb nicht hier, obwohl es einen Zähler hätte:
 * Es wäre dann an zwei Stellen anklickbar, und die Reiterleiste ist Bestand mit
 * eigenen Akzeptanzkriterien.
 *
 * Alle Zahlen stammen aus `GET /billing/summary` — demselben Aufruf, den
 * `AppRail` ohnehin absetzt. Gleicher Query-Key, also **kein zusätzlicher
 * Netzwerkaufruf**, nur eine zweite Anzeige derselben Antwort.
 */
function GeldSidebar() {
  const summary = useBillingSummary()
  const search = useRouterState({
    select: (state) => state.location.search as { overdue?: boolean; tab?: string },
  })
  // Der Reiter zählt mit: Steht man auf *Zahlungsmatrix* oder *Klärfälle*,
  // ist **keine** der beiden Ansichten die gezeigte. Bis Spec 08 stand hier
  // `search.tab !== 'klaerfaelle'` — mit einem dritten Reiter markierte das
  // fälschlich „Alle offenen", sobald *Zahlungsmatrix* aktiv war (Spec 08,
  // „Was 08 an Bestehendem ändert", Punkt 4). Ohne die richtige Bedingung
  // stünde „Alle offenen" dort markiert da, obwohl daneben eine ganz andere
  // Liste steht.
  const isOpenItems = search.tab === 'offene-posten'
  const isOverdue = search.overdue === true

  return (
    <ContextSidebar title="Geld">
      <SidebarSection title="Ansichten">
        <SidebarItem
          to="/geld"
          search={{ tab: 'offene-posten', overdue: false }}
          label="Alle offenen"
          testId="sidebar-geld-offen"
          {...(summary.data === undefined ? {} : { count: summary.data.open.count })}
          isActive={isOpenItems && !isOverdue}
        />
        <SidebarItem
          to="/geld"
          search={{ tab: 'offene-posten', overdue: true }}
          label="Überfällig"
          testId="sidebar-geld-ueberfaellig"
          {...(summary.data === undefined ? {} : { count: summary.data.overdue.count })}
          isAlert
          isActive={isOpenItems && isOverdue}
        />
      </SidebarSection>
    </ContextSidebar>
  )
}

/**
 * Die Sidebar des Bereichs *Energie* (Spec 46a, „Web · Filter-Form"): „Alle
 * Zähler" und je Haus mit Zähler — die Häuser und ihre Anzahl liefert
 * `GET /energy/meters` (`houses`). Dieselbe Abfrage wie die Liste in
 * `routes/Energie.tsx` (gleicher Query-Key über `?haus=`), also **kein
 * zusätzlicher Netzwerkaufruf**. Die Sektion „Verträge" kommt mit 46c.
 */
function EnergieSidebar() {
  const haus = useRouterState({ select: (state) => (state.location.search as { haus?: string }).haus })
  const houses = useMeters(haus).data?.houses ?? []

  return (
    <ContextSidebar title="Energie">
      {/* Ohne Häuser mit Zählern entfällt die Leiste (Spec 46a, „Haus-Filter"). */}
      {houses.length > 0 && (
        <SidebarSection title="Zähler">
          <HouseFilter houses={houses} selected={haus} form="sidebar" />
        </SidebarSection>
      )}
    </ContextSidebar>
  )
}

/**
 * Die Ansichten des Bereichs *Dokumente* (Spec 09, „Oberfläche · Wo"):
 * „Alle" · „Ohne Zuordnung" · je Art — dieselbe Bauform wie
 * `GeldSidebar`. Die acht Arten kommen aus `GET /documents/categories`
 * (`useDocumentCategories`, staleTime `Infinity`) — derselbe Aufruf, den
 * `document-category` im Sheet ohnehin absetzt, kein zusätzlicher
 * Netzwerkaufruf für die Sidebar. Keine Zahl an den Art-Einträgen: Es gibt
 * keinen Endpunkt, der je Art zählt, und ADR 0011 verlangt „Alle"/„Ohne
 * Zuordnung" reichen für den Zähler an der Symbolleiste.
 */
function DokumenteSidebar() {
  const summary = useDocumentSummary()
  const categories = useDocumentCategories()
  // `useRouterState` mit Cast, nicht `useSearch({ from: '/dokumente' })` —
  // diese Komponente hängt am `rootRoute`-Baum (`Sidebar()`), nicht *in* der
  // `/dokumente`-Route selbst; dieselbe Bauform wie `GeldSidebar`
  // oben.
  const search = useRouterState({ select: (state) => state.location.search as { view?: string; q?: string } })
  const view = toDokumenteView(search.view)
  const q = search.q ?? ''

  return (
    <ContextSidebar title="Dokumente">
      <SidebarSection title="Ansichten">
        <SidebarItem
          to="/dokumente"
          search={{ view: 'all', q }}
          label="Alle"
          testId="dokumente-view-all"
          {...(summary.data === undefined ? {} : { count: summary.data.total })}
          isActive={view === 'all'}
        />
        <SidebarItem
          to="/dokumente"
          search={{ view: 'unfiled', q }}
          label="Ohne Zuordnung"
          testId="dokumente-view-unfiled"
          {...(summary.data === undefined ? {} : { count: summary.data.unfiled })}
          isActive={view === 'unfiled'}
        />
      </SidebarSection>
      {categories.isSuccess && categories.data.length > 0 && (
        <SidebarSection title="Nach Art">
          {categories.data.filter((category) => isCategoryView(category.value)).map((category) => (
            <SidebarItem
              key={category.value}
              to="/dokumente"
              search={{ view: category.value as ReturnType<typeof toDokumenteView>, q }}
              label={category.label}
              testId={`dokumente-view-${category.value}`}
              isActive={view === category.value}
            />
          ))}
        </SidebarSection>
      )}
      {/* Spec 24c, Oberfläche: „Verwalten" unter „Nach Art" — die
       * Absenderliste ist eine Ansicht in *Dokumente*, kein Bereich, kein
       * Reiter. `q` bleibt hier absichtlich leer: Die Absenderliste hat ihre
       * eigene Suche (`sender-search`), nicht die der Dokumentliste. */}
      <SidebarSection title="Verwalten">
        <SidebarItem
          to="/dokumente"
          search={{ view: 'senders', q: '' }}
          label="Absender"
          testId="dokumente-view-senders"
          isActive={view === 'senders'}
        />
      </SidebarSection>
    </ContextSidebar>
  )
}

const TAB_IDS = ['stammdaten', 'dokumente', 'aufgaben', 'termine', 'geld'] as const
type TabId = (typeof TAB_IDS)[number]

function toTabId(value: unknown): TabId | undefined {
  return TAB_IDS.includes(value as TabId) ? (value as TabId) : undefined
}

/** Die Reiterleiste im Bereich *Geld* (`scope-geld-tabs`, Spec 04c, „Die
 * Reiterleiste im Bereich Geld"). Derselbe Grundsatz wie `toTabId`: ein
 * unbekannter Wert landet auf *Offene Posten*, statt auf einer Fehlerseite.
 * Spec 08 ergänzt `'matrix'` an **zweiter** Stelle (*Offene Posten ·
 * Zahlungsmatrix · Klärfälle*, AK-483). */
const GELD_TAB_IDS = ['offene-posten', 'matrix', 'klaerfaelle'] as const
type GeldTabId = (typeof GELD_TAB_IDS)[number]

function toGeldTabId(value: unknown): GeldTabId {
  return GELD_TAB_IDS.includes(value as GeldTabId) ? (value as GeldTabId) : 'offene-posten'
}

/** Das Fenster der Zahlungsmatrix in der URL (`?von=YYYY-MM`, Spec 08,
 * „Zustand in der URL"). Ein unbrauchbarer Wert (fehlt, falsches Format)
 * landet auf der Vorgabe — das Fenster endet dann im laufenden Monat, wie
 * der Server ohne `from`-Parameter default — statt auf einer Fehlerseite;
 * dieselbe Zurückhaltung wie `toGeldTabId`. */
const GELD_VON_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/

function toGeldVon(value: unknown): string | undefined {
  return typeof value === 'string' && GELD_VON_PATTERN.test(value) ? value : undefined
}

/** Suchparameter von `/geld`. `von` ist `von?: string | undefined`, nicht
 * `von?: string` — das `| undefined` lässt ein `search`-Literal den Schlüssel
 * weiterhin ganz weglassen (er bleibt optional), erlaubt `validateSearch`
 * unten aber zusätzlich, ihn **ausdrücklich** auf `undefined` zu setzen.
 *
 * **Fund, kein Geschmack.** Mit reinem `von?: string` (bis `08-
 * zahlungsmatrix.spec.ts`, AK-484) übernahm der Router bei einem
 * unbrauchbaren Wert in der URL (`von=Quatsch`) weiterhin den rohen
 * String — `validateSearch` ließ den Schlüssel bei einer ungültigen
 * Eingabe zwar aus seiner Rückgabe weg, `exactOptionalPropertyTypes`
 * verbot aber gerade die einzige Form, die ihn wirklich **löscht**
 * (`von: undefined`), und der Router füllte die fehlende Angabe
 * offenbar aus dem unvalidierten Ausgangswert nach. Ein Vertrag der
 * Journey deckte das auf: `von=Quatsch` landete unverändert als
 * `from=Quatsch-01` in `GET /billing/matrix` und der Server lehnte mit
 * 422 ab, statt auf die Vorgabe zurückzufallen (Spec 08, „Zustand in
 * der URL"). Mit `von?: string | undefined` schreibt `validateSearch`
 * jetzt in jedem Fall — gültig, ungültig oder ganz ohne Wert — einen
 * eigenen `von`-Eintrag, der den rohen URL-Wert immer überschreibt. */
interface GeldSearchParams {
  overdue: boolean
  tenant: string | undefined
  scope: string | undefined
  tab: GeldTabId
  von?: string | undefined
  contract?: string | undefined
}

/**
 * Das Konto — unten in der Symbolleiste, weil es zu keinem Bereich gehört
 * (ADR 0011). Damit ist auch die seit M0b offene Frage beantwortet, wo
 * „Abmelden" sitzt (ADR 0007 ließ sie ausdrücklich offen).
 *
 * Seit Spec 05a/25 drei Einträge in fester Reihenfolge, dieselben wie auf iOS
 * (`account-sheet`): Zugriffsverwaltung, **Passwort ändern**, Abmelden.
 * `account-access` erscheint **nur für interne Nutzer** — aus `is_external`
 * in `GET /access/me`, nicht aus einer Vermutung im Client (Spec: „Das
 * Ausblenden ist Bequemlichkeit, kein Schutz; der Schutz steht am
 * Endpunkt"). `account-password` (Spec 25, Oberfläche A) erscheint für
 * **jeden** — auch extern, das eigene Passwort ändert jeder selbst
 * (Fachregel 4). Der bestehende Bezeichner `rail-konto-abmelden` wurde zu
 * `account-signout` — geprüft folgenlos, kein Test und keine Journey
 * verwendete ihn (Spec 05a, „Konto-Menü").
 *
 * „Einstellungen" nennt ADR 0011 als weiteren Eintrag. Er steht hier
 * **nicht**, solange es nichts einzustellen gibt — ein Menüpunkt, der nichts
 * tut, ist schlechter als einer, der fehlt.
 *
 * **Zwei Einstiege, ein Bauteil** (Spec 25, Designabnahme-Auflage B1): Am
 * Schreibtisch sitzt der Auslöser unten in der Symbolleiste (`variant="rail"`,
 * Menü öffnet nach rechts), unter 768px im neuen mobilen Streifen
 * (`variant="mobile"`, Menü öffnet nach unten — sonst läge es außerhalb des
 * Fensters). `RootLayout` mountet pro Bildschirmbreite nur eine der beiden
 * Varianten (`useIsDesktopViewport`), nie beide zugleich.
 */
/** Exportiert für `router.AccountMenu.bild.test.tsx` (Auflage 8,
 * Tor-B-Designreview: `rail-konto-menu` mit seinen drei Einträgen war
 * ungeprüft). */
export function AccountMenu({ variant }: { readonly variant: 'rail' | 'mobile' }) {
  const { email, signOut } = useAuth()
  const me = useMe()
  const initials = (email ?? '?').slice(0, 2).toUpperCase()
  const [isPasswordOpen, setIsPasswordOpen] = useState(false)
  const [doneMessage, setDoneMessage] = useState<string | null>(null)

  return (
    <>
      <PopoverMenu
        triggerTestId="rail-konto"
        triggerLabel="Konto"
        // Am Telefon ist der Kreis selbst das Ziel: 44px (`--tap-min`), sonst
        // bliebe er 32px breit (Tor B LUK-45, F1). Die Symbolleiste bleibt bei 32px.
        triggerClassName={`bg-accent/10 text-accent text-label flex size-8 items-center justify-center rounded-full font-semibold${variant === 'mobile' ? ' min-w-(--tap-min)' : ''}`}
        menuTestId="rail-konto-menu"
        menuClassName={
          variant === 'rail'
            ? 'border-border bg-surface rounded-card absolute bottom-0 left-full z-50 ml-2 w-56 border'
            : 'border-border bg-surface rounded-card absolute top-full right-0 z-50 mt-2 w-56 border'
        }
        trigger={initials}
      >
        {(close) => (
          <>
            <p className="border-border text-label text-muted truncate border-b px-3 py-2">{email ?? 'Angemeldet'}</p>
            {me.data?.is_external === false && (
              <Link
                to="/zugriff"
                role="menuitem"
                data-testid="account-access"
                onClick={close}
                className="text-body text-text hover:bg-bg flex w-full items-center px-3 py-2 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
              >
                Zugriffsverwaltung
              </Link>
            )}
            <button
              type="button"
              role="menuitem"
              data-testid="account-password"
              onClick={() => {
                close()
                setIsPasswordOpen(true)
              }}
              className="text-body text-text hover:bg-bg flex w-full items-center px-3 py-2 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
            >
              Passwort ändern
            </button>
            <button
              type="button"
              role="menuitem"
              data-testid="account-signout"
              onClick={() => {
                void signOut()
              }}
              className="text-body text-status-overdue hover:bg-bg flex w-full items-center px-3 py-2 text-left transition-colors duration-(--duration-fast) ease-(--ease-out)"
            >
              Abmelden
            </button>
          </>
        )}
      </PopoverMenu>

      <AccountPasswordSheet
        open={isPasswordOpen}
        onOpenChange={setIsPasswordOpen}
        email={email ?? ''}
        onSaved={() => {
          setDoneMessage('Passwort geändert.')
        }}
      />
      <ConfirmationStrip
        testId="account-password-done"
        message={doneMessage}
        onDismiss={() => {
          setDoneMessage(null)
        }}
      />
    </>
  )
}

function RootLayout() {
  const refreshEntities = useRouterState({
    select: (state) => state.location.pathname.startsWith('/objekt')
      || typeof (state.location.search as { contract?: unknown }).contract === 'string',
  })
  // Ein Besitzer auch bei gleichzeitig sichtbarem Objekt und Vertragsmodal.
  useEntityProjectionRefresh(refreshEntities)
  // Spec 25, Designabnahme-Auflage B1: unter 768px trägt der mobile Streifen
  // den Kontomenü-Auslöser, den die Symbolleiste dort nicht mehr mountet
  // (`AppRail`) — dieselbe Umschaltung, an derselben Stelle entschieden.
  const isDesktop = useIsDesktopViewport()
  return (
    <AppShell rail={<AppRail />} tree={<Sidebar />} mobileAccount={isDesktop ? null : <AccountMenu variant="mobile" />}>
      <Outlet />
      <ContractModal />
    </AppShell>
  )
}

const rootRoute = createRootRoute({ component: RootLayout })

interface ContractSearchParam {
  contract?: string | undefined
  contractFacet?: import('@/lib/search-params').ContractFacet | undefined
}

const kalenderRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  // `ab` und `view` (Spec 45): `lib/kalender-view.ts` — `ab` gilt nur in den
  // vier Wochen, `view` kennt `ueberfaellig` und `meine`.
  validateSearch: (search: Record<string, unknown>): KalenderSearch & ContractSearchParam => ({
    ...parseKalenderSearch(search),
    ...contractSearch(search.contract),
    ...contractFacetSearch(search.contractFacet),
  }),
  component: Kalender,
})

const geldRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/geld',
  // `overdue=1`, nicht der TanStack-Standard `overdue=true` (der Prosa-Beleg
  // in Spec 04a, „Reiter Offene Posten": „/geld?overdue=1&tenant=<id>…") wäre
  // eine eigene Serialisierung wert gewesen — hier reicht die eingebaute
  // Boolean-Kodierung des Routers, AK-296 verlangt nur, dass ein geteilter
  // Link dieselbe Ansicht zeigt, keine bestimmte Zeichenkette.
  validateSearch: (search: Record<string, unknown>): GeldSearchParams => {
    const von = toGeldVon(search.von)
    return {
      overdue: search.overdue === true,
      tenant: typeof search.tenant === 'string' && search.tenant !== '' ? search.tenant : undefined,
      scope: typeof search.scope === 'string' && search.scope !== '' ? search.scope : undefined,
      tab: toGeldTabId(search.tab),
      // Spec 08, „Zustand in der URL" — nur der Reiter *Zahlungsmatrix* liest
      // ihn, bleibt aber beim Wechseln auf die anderen beiden Reiter erhalten
      // (dieselbe Zurückhaltung wie `overdue`/`tenant`/`scope`). **Immer**
      // gesetzt, auch als `undefined` — sonst behält der Router bei einer
      // ungültigen Eingabe den rohen, nie geprüften Wert (Fund, siehe
      // `GeldSearchParams`).
      von,
      ...contractSearch(search.contract),
      ...contractFacetSearch(search.contractFacet),
    }
  },
  component: Geld,
})

/** *Aufgaben* ist kein Bereich mehr (ADR 0033, Spec 45 R14): Ein Lesezeichen
 * oder geteilter Link auf `/aufgaben` landet bei „Meine Aufgaben" im Kalender
 * statt auf einer 404-Seite. `redirect` in `beforeLoad` — kein Bildschirm. */
const aufgabenRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/aufgaben',
  beforeLoad: () => {
    // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router: `redirect()` wird geworfen (Bibliotheksvertrag)
    throw redirect({ to: '/', search: { view: 'meine' } })
  },
})

/** Bereich *Energie* (Spec 45, gefüllt mit Spec 46a): `haus` (Filter) und
 * `zaehler` (geöffnetes Detail) stehen in der URL. */
const energieRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/energie',
  validateSearch: energieSearch,
  component: Energie,
})

/** Bereich *Dokumente* (Spec 09, „Oberfläche · Wo"). `view` trägt die
 * gewählte Ansicht, `q` den Suchtext — dieselbe Bauform wie `tab`/`view` an
 * `/geld`. `q` bleibt beim Wechseln der Ansicht erhalten
 * (`DokumenteSidebar` reicht es an jedem Link mit), nicht umgekehrt: Ein
 * geteilter Link mit `q` zeigt beim Empfänger dieselbe gefilterte Liste. */
const dokumenteRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/dokumente',
  validateSearch: (search: Record<string, unknown>): { view: ReturnType<typeof toDokumenteView>; q: string } & ContractSearchParam => ({
    view: toDokumenteView(search.view),
    q: typeof search.q === 'string' ? search.q : '',
    ...contractSearch(search.contract),
    ...contractFacetSearch(search.contractFacet),
  }),
  component: Dokumente,
})

const objectListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/objekt',
  component: ObjectList,
})

const objectDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/objekt/$entityId',
  validateSearch: (search: Record<string, unknown>) => {
    const tab = toTabId(search.tab)
    return { tab, ...contractSearch(search.contract), ...contractFacetSearch(search.contractFacet) }
  },
  component: ObjectDetail,
})

const searchRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/suchen',
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === 'string' ? search.q : '',
  }),
  component: Search,
})

/** `screen-access` (Spec 05a) — hängt am Konto, nicht an einem `rail-*`-
 * Eintrag: `account-access` verlinkt direkt hierher, kein siebter Bereich. */
const accessRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zugriff',
  component: Access,
})

/** `screen-access-user` (Spec 05b, „Personenansicht") — aus
 * `access-user-row-<id>` erreichbar, aber eine eigene Adresse: Der
 * `person`-Block von `GET /access/users/{id}/grants` trägt den ganzen Kopf,
 * der Bildschirm lädt beim Direktaufruf nichts nach. */
const accessUserRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/zugriff/$userId',
  component: AccessUser,
})

const routeTree = rootRoute.addChildren([
  kalenderRoute,
  geldRoute,
  energieRoute,
  aufgabenRoute,
  dokumenteRoute,
  objectListRoute,
  objectDetailRoute,
  searchRoute,
  accessRoute,
  accessUserRoute,
])

export const router = createRouter({
  routeTree,
  parseSearch: parseAppSearch,
  // Ein unbekannter Pfad ist im Alltag ein Tippfehler in der Adresszeile oder
  // ein Link auf ein gelöschtes Objekt. Beides gehört auf „Kalender" (Route
  // `/`), nicht auf eine 404-Seite, die man wegklicken muss.
  //
  // `replace`, damit der Zurück-Knopf nicht auf den kaputten Pfad zeigt und
  // sofort wieder hierher umleitet.
  defaultNotFoundComponent: () => <Navigate to="/" replace />,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
